import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, AlertTitle, Tooltip } from '@mui/material';
import InputBox from '../../atomic/inputbox/InputBox';
import AtomicButton from '../../atomic/button/Button';
import { isIpad } from '../../utils/ScreenSizeHelper';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import PHONE_SMS from '../../utils/assets/png/phone_sms_icon_white_bg.png';
import { useNavigate } from 'react-router-dom';

const OTP_EXPIRE_STORAGE_KEY_PREFIX = 'teamPhoneVerifyOtpExpire_';
/** When the API does not return `exptime`, use a client-side resend cooldown after successful `generateOtp`. */
const DEFAULT_RESEND_COOLDOWN_SEC = 60;

const getOtpExpireStorageKey = (teamUserId) => `${OTP_EXPIRE_STORAGE_KEY_PREFIX}${teamUserId || ''}`;

/** @param {unknown} raw */
function toE164PhoneNo(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  if (s.startsWith('+')) return s;
  const digits = s.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length > 0) return `+${digits}`;
  return null;
}

const PhoneVerifyOtp = () => {
  const {
    onLoadRegBranchResponse,
    setOnLoadRegBranchResponse,
    isLoggedIn,
    regBranchSession,
    OnPhoneVerify,
    onPhoneVerifyResponse,
    branchSessionLogout,
  } = useContext(GlobalStateDataContext);
  const navigate = useNavigate();

  const teamInfo = regBranchSession?.teamInfo;
  const team_user_id = useMemo(() => String(teamInfo?.team_user_id ?? '').trim(), [teamInfo?.team_user_id]);
  const targetPhone = useMemo(() => toE164PhoneNo(teamInfo?.phone_no), [teamInfo?.phone_no]);

  const needsPhoneVerify =
    !!teamInfo && teamInfo.phone_verified_status !== true;

  const [otp, setOtp] = useState('');
  const [validationError, setValidationError] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (!isLoggedIn || !needsPhoneVerify || !team_user_id || !targetPhone) {
      return;
    }

    const storageKey = getOtpExpireStorageKey(team_user_id);
    const storedExpire = localStorage.getItem(storageKey);
    const exptime = storedExpire ? parseInt(storedExpire, 10) : null;
    const now = Date.now();

    if (exptime && !Number.isNaN(exptime) && exptime > now) {
      const seconds = Math.floor((exptime - now) / 1000);
      setRemainingSeconds(seconds);
      setCanResend(false);
      return;
    }

    const payload = {
      team_user_id,
      phone_no: targetPhone,
      serviceType: 'generateOtp',
    };
    void OnPhoneVerify(payload);
    // Re-run when on-load session snapshot changes (cache vs network).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLoadRegBranchResponse, regBranchSession]);

  useEffect(() => {
    const inner = onPhoneVerifyResponse?.data?.data;
    const exptime =
      inner?.exptime ??
      inner?.data?.exptime ??
      onPhoneVerifyResponse?.data?.data?.data?.exptime;

    if (exptime) {
      const now = Date.now();
      const seconds = Math.max(0, Math.floor((exptime - now) / 1000));
      setRemainingSeconds(seconds);
      setCanResend(seconds <= 0);

      if (team_user_id) {
        localStorage.setItem(getOtpExpireStorageKey(team_user_id), String(exptime));
      }
      return;
    }

    const msg = onPhoneVerifyResponse?.message;
    const success = onPhoneVerifyResponse?.success === true;
    if (success && msg !== 'OTP Verified' && team_user_id) {
      const expires = Date.now() + DEFAULT_RESEND_COOLDOWN_SEC * 1000;
      localStorage.setItem(getOtpExpireStorageKey(team_user_id), String(expires));
      setRemainingSeconds(DEFAULT_RESEND_COOLDOWN_SEC);
      setCanResend(false);
    }
  }, [onPhoneVerifyResponse, team_user_id]);

  useEffect(() => {
    if (remainingSeconds === null || remainingSeconds <= 0) {
      return;
    }

    const timerId = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(timerId);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerId);
  }, [remainingSeconds]);

  const formatTime = (seconds) => {
    if (seconds === null || seconds < 0) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    const mm = mins.toString().padStart(2, '0');
    const ss = secs.toString().padStart(2, '0');
    return `${mm}:${ss}`;
  };

  const patchTeamInfoInState = (mutator) => {
    setOnLoadRegBranchResponse((prev) => {
      const block = prev?.data?.data?.data;
      if (!block || typeof block !== 'object') return prev;
      const key =
        block.TeamInfo != null ? 'TeamInfo' : block.teamInfo != null ? 'teamInfo' : null;
      if (!key) return prev;
      const info = block[key];
      if (!info) return prev;
      return {
        ...prev,
        data: {
          ...prev.data,
          data: {
            ...prev.data.data,
            data: {
              ...block,
              [key]: mutator(info),
            },
          },
        },
      };
    });
  };

  const verifyOtp = async () => {
    if (!otp || otp.length !== 5) {
      setValidationError('Please enter the 5 digit verification code.');
      return;
    }
    if (!team_user_id || !targetPhone) {
      setValidationError('Missing team or phone information.');
      return;
    }

    setValidationError(null);

    const OtpVerifyPayload = {
      team_user_id,
      phone_no: targetPhone,
      serviceType: 'verifyOtpNo',
      otpNo: otp,
    };

    const response = await OnPhoneVerify(OtpVerifyPayload);

    if (
      response &&
      response.hasError === false &&
      response.data?.success &&
      response.data?.CustomMessage === 'OTP Verified'
    ) {
      localStorage.removeItem(getOtpExpireStorageKey(team_user_id));
      setRemainingSeconds(null);
      setCanResend(true);
      patchTeamInfoInState((prev) => ({
        ...prev,
        phone_verified_status: true,
      }));
    }
  };

  const handleResendOtp = () => {
    if (!canResend) return;
    if (!team_user_id || !targetPhone) return;
    setOtp('');
    setValidationError(null);
    setCanResend(false);

    const payload = {
      team_user_id,
      phone_no: targetPhone,
      serviceType: 'generateOtp',
    };
    void OnPhoneVerify(payload);
  };

  if (!teamInfo) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#3F3F6E' }}>
        Phone verification is only available for team accounts with profile data loaded.
      </div>
    );
  }

  if (teamInfo.phone_verified_status === true) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#2E7D32' }}>
        Your phone number is verified.
      </div>
    );
  }

  if (!targetPhone) {
    return (
      <div style={{ padding: '24px', maxWidth: '480px', margin: '0 auto', color: '#3F3F6E' }}>
        <Alert severity='warning' sx={{ textAlign: 'left' }}>
          <AlertTitle>No phone number on file</AlertTitle>
          Add a valid mobile number to your team profile before SMS verification can run.
        </Alert>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        marginTop: '50px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          backgroundColor: 'white',
          width: '640px',
          height: 'auto',
          borderRadius: '8px',
          boxShadow: 'rgb(189, 189, 189) 0px 3px 6px 0px',
          padding: '32px 40px',
          border: '1px solid rgb(230, 230, 230)',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            height: '60px',
            marginRight: '10px',
            cursor: 'pointer',
          }}
        >
          <img
            src={PHONE_SMS}
            width={180}
            alt='PHONE_SMS'
            loading='eager'
            onError={(e) => {
              console.warn('PHONE_SMS failed to load, retrying...');
              e.target.onerror = null;
              e.target.src = PHONE_SMS + `?v=${Date.now()}`;
            }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div
            style={{
              marginBottom: '20px',
              color: '#3F3F6E',
              fontSize: isIpad ? '24px' : '28px',
              fontWeight: '700',
            }}
          >
            Phone verification
          </div>

          {targetPhone && (
            <div
              style={{
                marginBottom: '8px',
                fontSize: '14px',
                color: '#3F3F6E',
                fontFamily: 'Inter Regular',
              }}
            >
              We have sent a verification code to:{' '}
              <span style={{ fontFamily: 'Inter SemiBold' }}>{targetPhone}</span>
            </div>
          )}

          {validationError && (
            <Alert
              severity='error'
              sx={{
                width: '385px',
                border: 'solid 1px #E74C3C',
                marginBottom: '10px',
                backgroundColor: '#FDEDEB',
                color: 'red',
              }}
            >
              <AlertTitle sx={{ fontSize: '14px', fontWeight: 600 }}>Error</AlertTitle>
              <ul>
                <li style={{ fontSize: '12px' }}>
                  <strong>{validationError}</strong>
                </li>
              </ul>
            </Alert>
          )}
          {onPhoneVerifyResponse?.message && (
            <Alert
              severity={onPhoneVerifyResponse?.success ? 'success' : 'error'}
              sx={{
                width: '385px',
                border: onPhoneVerifyResponse?.success
                  ? 'solid 1px #04cc3dff'
                  : 'solid 1px #ff4430ff',
                marginBottom: '10px',
                backgroundColor: onPhoneVerifyResponse?.success ? '#E8F5E9' : '#FDEDEB',
                color: onPhoneVerifyResponse?.success ? '#2E7D32' : '#f3a097ff',
              }}
            >
              <AlertTitle sx={{ fontSize: '14px', fontWeight: 600 }}>
                {onPhoneVerifyResponse?.success ? 'Success' : 'Error'}
              </AlertTitle>
              <div style={{ fontSize: '12px' }}>{onPhoneVerifyResponse?.message}</div>
            </Alert>
          )}

          <div
            style={{
              marginBottom: '10px',
              fontSize: '14px',
              color: '#3F3F6E',
              fontFamily: 'Inter Regular',
            }}
          >
            Enter the 5 digit verification code you received to confirm your phone number.
          </div>

          <InputBox
            type='number'
            label='Verification code'
            size='small'
            sx={{
              width: '400px',
              height: 'auto',
              margin: '10px 5px 20px 0px',
            }}
            name='otp'
            value={otp}
            maxLength='5'
            onChange={(event) => {
              event.preventDefault();
              const value = event.target.value;

              if (value?.length <= 5) {
                setOtp(value);
              }
            }}
          />

          <AtomicButton
            label='Verify OTP'
            handleSubmit={verifyOtp}
            style={{ backgroundColor: '#181436', width: '90%' }}
            loading={onPhoneVerifyResponse?.loading}
            disabled={
              !otp || otp.length !== 5 || onPhoneVerifyResponse?.message === 'Invalid Phone Number'
            }
          />
          <div
            style={{
              marginTop: '12px',
              fontSize: '13px',
              color: '#3F3F6E',
              fontFamily: 'Inter Regular',
            }}
          >
            <span style={{ fontFamily: 'Inter Regular' }}>
              {onPhoneVerifyResponse?.message === 'Invalid Phone Number'
                ? 'Invalid Phone Number. '
                : 'Change phone number. '}
            </span>
            <Tooltip title='function not available as of now' placement='top' arrow>
              <span
                style={{
                  display: 'inline-block',
                  fontFamily: 'Inter Bold',
                  fontWeight: 700,
                  color: '#A0A0A0',
                  cursor: 'not-allowed',
                  textDecoration: 'underline',
                  userSelect: 'none',
                }}
              >
                Update Now ?
              </span>
            </Tooltip>

            {onPhoneVerifyResponse?.message !== 'Invalid Phone Number' && (
              <span style={{ display: 'block', marginTop: '8px' }}>
                {remainingSeconds !== null && remainingSeconds > 0 ? (
                  <span>
                    Resend OTP in{' '}
                    <span style={{ fontFamily: 'Inter Bold' }}>
                      {' '}
                      {formatTime(remainingSeconds)}
                    </span>
                  </span>
                ) : (
                  <span>You can resend the OTP now.</span>
                )}
                <span
                  onClick={handleResendOtp}
                  style={{
                    marginLeft: '8px',
                    color: canResend ? '#5078e6' : '#A0A0A0',
                    cursor: canResend ? 'pointer' : 'default',
                    fontFamily: 'Inter SemiBold',
                  }}
                >
                  Resend OTP
                </span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneVerifyOtp;
